/**
 * Wrap a workspace script that follows the **default-export handler** contract
 * so a REPL runner can invoke it with a typed args object.
 *
 * The eval / executeScript "run a file with args" path used to prepend
 * `const args = {…}` to the source and rely on the script referencing a bespoke
 * ambient `args` global (a `declare const args` per script that TypeScript
 * couldn't verify against the value actually injected). Instead a script now
 * exports a default function that receives its args as an ordinary typed
 * parameter — the shape every serverless TS runtime uses
 * (`export default { fetch(req, env) }`, `export const handler = (event) => …`) —
 * so the args type-check natively, per-script, with no ambient declaration.
 *
 * We rewrite the module's leading `export default <expr>` into a local
 * `const __scriptDefault = <expr>` binding and append a trailing
 * `await __scriptDefault(<args>)` call. Both sandbox REPL mechanics capture a
 * trailing expression as the result (see `wrapForRepl`), so the handler's
 * resolved return value becomes the eval result with no runner-specific
 * plumbing.
 *
 * A script with no `export default` is returned unchanged — legacy top-level
 * code and inline snippets that don't take args still run as-is — so this is
 * safe to apply on every file-path eval.
 */

// Matches the module's leading `export default ` at the start of a line. The
// `m` flag anchors to line starts; without `g` only the first occurrence is
// rewritten (a handler module has exactly one default export).
const EXPORT_DEFAULT = /^[ \t]*export[ \t]+default[ \t]+/m;

/**
 * Rewrite a default-export handler script into a self-invoking module.
 *
 * @param source the raw TS/TSX script source
 * @param args   the args object handed to the handler (JSON-serialised inline)
 * @returns the source to compile + evaluate; unchanged if there's no default export
 */
export function wrapScriptWithArgs(source: string, args: unknown): string {
  if (!EXPORT_DEFAULT.test(source)) return source;
  const rewritten = source.replace(EXPORT_DEFAULT, "const __scriptDefault = ");
  // Leading `;` terminates the const declaration so a `function`/arrow
  // expression isn't parsed as an IIFE over the next line (`}\n(…)` → call).
  // The trailing expression (no semicolon) is what the REPL wrap captures as
  // the result, so the handler's return value surfaces as the eval result.
  return `${rewritten}\n;(await __scriptDefault(${JSON.stringify(args ?? {})}))`;
}
